import { Fancybox } from '@fancyapps/ui';

const cfg = window.userProfileConfig || {};

// ─────────────────────────────────────────────────────────────────────────────
// GF field IDs come from Site Options → Form Settings (Mightily\Utils\FormMap), passed
// in as cfg.fields.{form}.{role}. 0 = not mapped (skipped).
// ─────────────────────────────────────────────────────────────────────────────
const profileIds = (cfg.fields && cfg.fields.editProfile) || {};
const programIds = (cfg.fields && cfg.fields.program) || {};

// Data key returned by get_user_profile_data → GF field ID
const PROFILE_FIELD_MAP = {
	organization_name: profileIds.organization_name,
	phone_number: profileIds.phone_number,
	website: profileIds.website,
	email: profileIds.email,
};

// Data key returned by get_program_data (post fields, or ACF under data.acf) → GF field ID
const PROGRAM_FIELD_MAP = {
	post_title: programIds.title,
	post_content: programIds.description,
	phone: programIds.phone,
	email: programIds.email,
	website: programIds.website,
	price: programIds.price,
};

// Hidden field that stores the post ID — blank when adding (creates a new post)
const PROGRAM_POST_ID_FIELD = programIds.post_id;

const SCHEDULE_FIELDS = {
	schedule: programIds.schedule, // repeater
	days: programIds.schedule_days, // ↳ checkbox
	start: programIds.schedule_start, // ↳ time
	end: programIds.schedule_end, // ↳ time
	repeats: programIds.schedule_repeats, // ↳ select
	closed: programIds.closed_dates, // repeater
	closedDate: programIds.closed_date, // ↳ date (MM / DD / YYYY inputs)
	seasonStart: programIds.season_start, // date
	seasonEnd: programIds.season_end, // date
	note: programIds.schedule_note, // textarea
};

// Static form modals — no swipe gestures or drag-to-close; dragging fights
// with selecting/inputting text in the form fields.
const MODAL_OPTS = {
	dragToClose: false,
	Carousel: { gestures: false },
};

function removeMember(memberId, listItem) {
	if (!cfg.ajaxUrl || !cfg.nonce) return;

	fetch(cfg.ajaxUrl, {
		method: 'POST',
		headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
		body: new URLSearchParams({
			action:    'remove_organization_member',
			nonce:     cfg.nonce,
			member_id: memberId,
		}),
	})
		.then((r) => r.json())
		.then((response) => {
			if (!response.success) return;
			listItem.remove();
		});
}

function deleteProgram(postId, card) {
	if (!cfg.ajaxUrl || !cfg.nonce) return;

	fetch(cfg.ajaxUrl, {
		method: 'POST',
		headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
		body: new URLSearchParams({
			action:  'remove_program',
			nonce:   cfg.nonce,
			post_id: postId,
		}),
	})
		.then((r) => r.json())
		.then((response) => {
			if (!response.success) return;
			card.remove();
		});
}

function setGfField(formId, fieldId, value) {
	if (!formId || !fieldId) return;
	const el = document.getElementById(`input_${formId}_${fieldId}`);
	if (!el) return;

	if (el.tagName === 'TEXTAREA' || el.tagName === 'SELECT') {
		el.value = value;
		el.dispatchEvent(new Event('change', { bubbles: true }));
	} else {
		el.value = value;
		el.dispatchEvent(new Event('input', { bubbles: true }));
	}
}

// Check/select the inputs of an ACF-driven choice field (a GF field tagged
// with the `acf-choices--{name}` CSS class — see functions/gravityforms).
// Values come straight from ACF, so they match the input values verbatim.
function setAcfChoiceField(name, value) {
	const field = document.querySelector(`#modal-program .acf-choices--${CSS.escape(name)}`);
	if (!field) return;

	const values = (Array.isArray(value) ? value : [value])
		.filter((v) => v !== null && v !== undefined && v !== '')
		.map(String);

	field.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach((input) => {
		input.checked = values.includes(input.value);
	});
	field.querySelectorAll('select option').forEach((opt) => {
		opt.selected = values.includes(opt.value);
	});
}

function clearAcfChoiceFields() {
	document.querySelectorAll('#modal-program [class*="acf-choices--"]').forEach((field) => {
		field.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach((input) => {
			input.checked = false;
		});
		field.querySelectorAll('select').forEach((select) => {
			select.selectedIndex = select.multiple ? -1 : 0;
		});
	});
}

// ─── Schedule repeaters ──────────────────────────────────────────────────────

function setInput(el, value) {
	if (!el) return;
	el.value = value;
	el.dispatchEvent(new Event('change', { bubbles: true }));
}

function repeaterContainer(formId, fieldId) {
	return document.querySelector(`#field_${formId}_${fieldId} .gfield_repeater_container`);
}

function repeaterItems(container) {
	return container ? [...container.querySelectorAll(':scope > .gfield_repeater_items > .gfield_repeater_item')] : [];
}

// Blank every input in a repeater row (or any field container)
function clearInputs(scope) {
	scope.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach((input) => {
		input.checked = false;
	});
	scope.querySelectorAll('input[type="number"], input[type="text"], textarea').forEach((input) => setInput(input, ''));
	scope.querySelectorAll('select').forEach((select) => setInput(select, select.options[0] ? select.options[0].value : ''));
}

// Reset a repeater to one blank row. GF's own delete asks for confirmation
// and announces each removal, so extra rows are removed directly and GF's
// helpers re-index the names and fix the add/remove buttons.
function resetRepeater(formId, fieldId) {
	const container = repeaterContainer(formId, fieldId);
	const items = repeaterItems(container);
	if (!items.length) return;

	items.slice(1).forEach((item) => item.remove());
	clearInputs(items[0]);

	if (window.gform && window.gform.repeater) window.gform.repeater.reindexRepeaterLevel(container);
	if (window.gformToggleRepeaterButtons) window.gformToggleRepeaterButtons(container);
}

// GF loads its repeater module lazily once the form is visible, so the first
// time the modal opens the AJAX data can arrive before it exists.
function repeaterReady(timeout = 5000) {
	const ready = () => window.gform && window.gform.repeater && window.gform.repeater.addRepeaterItem;
	if (ready()) return Promise.resolve(true);
	return new Promise((resolve) => {
		const start = Date.now();
		const timer = setInterval(() => {
			if (ready() || Date.now() - start > timeout) {
				clearInterval(timer);
				resolve(!!ready());
			}
		}, 50);
	});
}

// Grow a repeater to `count` rows via GF's own add (keeps IDs/names right)
async function ensureRepeaterRows(formId, fieldId, count) {
	const container = repeaterContainer(formId, fieldId);
	if (!container || !(await repeaterReady())) return [];

	while (repeaterItems(container).length < count) {
		const items = repeaterItems(container);
		const addBtn = items[items.length - 1].querySelector(':scope > .gfield_repeater_buttons .add_repeater_item');
		if (!addBtn) break;
		await window.gform.repeater.addRepeaterItem(addBtn);
	}
	return repeaterItems(container);
}

// Sub-field container inside a repeater row (IDs are field_{form}_{field}-{index})
function rowField(row, formId, fieldId) {
	return row.querySelector(`[id^="field_${formId}_${fieldId}-"]`);
}

// Minutes after midnight → GF time inputs (hour / minute / AM-PM)
function setTimeField(field, minutes) {
	if (!field || minutes === null || minutes === undefined) return;
	const h24 = Math.floor(minutes / 60) % 24;
	setInput(field.querySelector('.gfield_time_hour input'), String(h24 % 12 || 12));
	setInput(field.querySelector('.gfield_time_minute input'), String(minutes % 60).padStart(2, '0'));
	setInput(field.querySelector('.gfield_time_ampm select'), h24 < 12 ? 'am' : 'pm');
}

// "Y-m-d" → GF date-field inputs (MM / DD / YYYY)
function setDateField(field, ymd) {
	if (!field) return;
	const [y, m, d] = (ymd || '').split('-');
	setInput(field.querySelector('.gfield_date_month input'), m || '');
	setInput(field.querySelector('.gfield_date_day input'), d || '');
	setInput(field.querySelector('.gfield_date_year input'), y || '');
}

function clearScheduleFields(formId) {
	const f = SCHEDULE_FIELDS;
	resetRepeater(formId, f.schedule);
	resetRepeater(formId, f.closed);
	setDateField(document.getElementById(`field_${formId}_${f.seasonStart}`), '');
	setDateField(document.getElementById(`field_${formId}_${f.seasonEnd}`), '');
	setGfField(formId, f.note, '');
}

// `schedule` comes from ProgramSchedule::get() via the get_program_data AJAX
async function populateScheduleFields(formId, schedule) {
	const f = SCHEDULE_FIELDS;
	clearScheduleFields(formId);
	if (!schedule) return;

	const rows = schedule.rows || [];
	if (rows.length) {
		const items = await ensureRepeaterRows(formId, f.schedule, rows.length);
		rows.forEach((row, i) => {
			const item = items[i];
			if (!item) return;
			const days = rowField(item, formId, f.days);
			if (days) {
				days.querySelectorAll('input[type="checkbox"]').forEach((input) => {
					input.checked = row.days.includes(input.value);
				});
			}
			setTimeField(rowField(item, formId, f.start), row.start);
			setTimeField(rowField(item, formId, f.end), row.end);
			const repeats = rowField(item, formId, f.repeats);
			if (repeats) setInput(repeats.querySelector('select'), row.repeats);
		});
	}

	const closed = schedule.closed || [];
	if (closed.length) {
		const items = await ensureRepeaterRows(formId, f.closed, closed.length);
		closed.forEach((date, i) => {
			if (items[i]) setDateField(rowField(items[i], formId, f.closedDate), date);
		});
	}

	setDateField(document.getElementById(`field_${formId}_${f.seasonStart}`), schedule.start);
	setDateField(document.getElementById(`field_${formId}_${f.seasonEnd}`), schedule.end);
	setGfField(formId, f.note, schedule.note || '');
}

// ─────────────────────────────────────────────────────────────────────────────

function clearProgramForm() {
	const formId = cfg.forms && cfg.forms.program;
	if (!formId) return;

	// Clear the post ID hidden field so a blank value signals "new post"
	if (PROGRAM_POST_ID_FIELD) {
		setGfField(formId, PROGRAM_POST_ID_FIELD, '');
	}

	Object.values(PROGRAM_FIELD_MAP).forEach((fieldId) => {
		if (fieldId) setGfField(formId, fieldId, '');
	});

	clearAcfChoiceFields();
	clearScheduleFields(formId);
	setDateField(document.getElementById(`field_${formId}_${programIds.expiration_date}`), '');
}

function fetchProfileAndPopulate() {
	if (!cfg.ajaxUrl || !cfg.nonce) return;

	fetch(cfg.ajaxUrl, {
		method: 'POST',
		headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
		body: new URLSearchParams({
			action: 'get_user_profile_data',
			nonce: cfg.nonce,
		}),
	})
		.then((r) => r.json())
		.then((response) => {
			if (!response.success) return;
			const data = response.data;
			const formId = cfg.forms && cfg.forms.editProfile;

			Object.entries(PROFILE_FIELD_MAP).forEach(([key, fieldId]) => {
				if (fieldId && data[key] !== undefined) {
					setGfField(formId, fieldId, data[key]);
				}
			});
		});
}

function fetchProgramAndPopulate(postId) {
	if (!cfg.ajaxUrl || !cfg.nonce || !postId) return;

	fetch(cfg.ajaxUrl, {
		method: 'POST',
		headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
		body: new URLSearchParams({
			action: 'get_program_data',
			nonce: cfg.nonce,
			post_id: postId,
		}),
	})
		.then((r) => r.json())
		.then((response) => {
			if (!response.success) return;
			const data = response.data;
			const formId = cfg.forms && cfg.forms.program;

			if (PROGRAM_POST_ID_FIELD) {
				setGfField(formId, PROGRAM_POST_ID_FIELD, postId);
			}

			Object.entries(PROGRAM_FIELD_MAP).forEach(([key, fieldId]) => {
				if (!fieldId) return;
				const value = data[key] !== undefined ? data[key] : (data.acf || {})[key];
				if (value !== undefined) {
					setGfField(formId, fieldId, value === null ? '' : value);
				}
			});

			// Pre-select the ACF-driven choice fields from the post's values
			clearAcfChoiceFields();
			Object.entries(data.acf || {}).forEach(([name, value]) => {
				setAcfChoiceField(name, value);
			});

			populateScheduleFields(formId, data.schedule);

			// ACF date_picker stores "Ymd" (older values: "Y-m-d H:i:s") → "Y-m-d"
			const rawExpires = String((data.acf || {}).program_expiration_date || '');
			const expires = /^\d{8}$/.test(rawExpires)
				? `${rawExpires.slice(0, 4)}-${rawExpires.slice(4, 6)}-${rawExpires.slice(6, 8)}`
				: rawExpires.slice(0, 10);
			setDateField(document.getElementById(`field_${formId}_${programIds.expiration_date}`), expires);
		});
}

function setProgramModalHeading(text) {
	const heading = document.querySelector('.js-program-modal-heading');
	if (heading) heading.textContent = text;
}

// Set when the add-member form is submitted while its modal stays open —
// the page reloads once that modal closes so the members list updates.
let memberAdded = false;

// Close the active Fancybox and reload to reflect saved changes.
function handleGfConfirmation(event, formId) {
	// Add Member keeps its modal open so the confirmation stays visible
	const addMemberFormId = Number(cfg.forms && cfg.forms.addMember) || 0;
	if (addMemberFormId && Number(formId) === addMemberFormId) {
		memberAdded = true;
		return;
	}

	const profileForms = [cfg.forms && cfg.forms.editProfile, cfg.forms && cfg.forms.program].filter(Boolean).map(Number);

	if (!profileForms.includes(Number(formId))) return;

	setTimeout(() => {
		Fancybox.close();
		window.location.reload();
	}, 1400);
}

export default {
	init() {
		// Edit Profile
		document.addEventListener('click', (e) => {
			if (!e.target.closest('.js-open-edit-profile')) return;
			Fancybox.show([{ src: '#modal-edit-profile', type: 'inline' }], MODAL_OPTS);
			fetchProfileAndPopulate();
		});

		// Edit Program — fetch existing data and populate
		document.addEventListener('click', (e) => {
			const trigger = e.target.closest('.js-open-edit-program');
			if (!trigger) return;
			const postId = trigger.dataset.postId;
			setProgramModalHeading('Edit Program');
			Fancybox.show([{ src: '#modal-program', type: 'inline' }], MODAL_OPTS);
			fetchProgramAndPopulate(postId);
		});

		// Add Program — clear the form so no stale values carry over
		document.addEventListener('click', (e) => {
			if (!e.target.closest('.js-open-add-program')) return;
			setProgramModalHeading('Add a Program');
			Fancybox.show([{ src: '#modal-program', type: 'inline' }], MODAL_OPTS);
			clearProgramForm();
		});

		// Add Member — modal stays open after submit; reload on close so the
		// members list reflects any additions
		document.addEventListener('click', (e) => {
			if (!e.target.closest('.js-open-add-member')) return;
			Fancybox.show([{ src: '#modal-add-member', type: 'inline' }], {
				...MODAL_OPTS,
				on: {
					destroy: () => {
						if (memberAdded) {
							memberAdded = false;
							window.location.reload();
						}
					},
				},
			});
		});

		// Remove member (leaders only)
		document.addEventListener('click', (e) => {
			const trigger = e.target.closest('.js-remove-member');
			if (!trigger) return;
			const memberId = trigger.dataset.memberId;
			const listItem = trigger.closest('.user-profile__member-item');
			if (listItem) removeMember(memberId, listItem);
		});

		// Delete program — confirm first; wp_delete_post trashes it (not
		// permanent), so this is recoverable from wp-admin if clicked by mistake
		document.addEventListener('click', (e) => {
			const trigger = e.target.closest('.js-delete-program');
			if (!trigger) return;
			if (!window.confirm('Delete this program? This can be undone from the admin dashboard.')) return;
			const postId = trigger.dataset.postId;
			const card = trigger.closest('.user-profile__program-card');
			if (card) deleteProgram(postId, card);
		});

		// GF confirmation → close modal + reload
		jQuery(document).on('gform_confirmation_loaded', handleGfConfirmation);
	},
};
